import { observer } from 'mobx-react-lite';
import React from 'react';
import { Controller } from 'react-hook-form';

import { useStores } from '@src/Common/Hooks/useStores';

import ButtonDefault from '@src/Components/Atoms/ButtonDefault/ButtonDefault';
import CustomLoading from '@src/Components/Atoms/CustomLoading/CustomLoading';
import InputArea from '@src/Components/Atoms/InputArea/InputArea';
import InputDate from '@src/Components/Atoms/InputDate/InputDate';
import InputFile from '@src/Components/Atoms/InputFile/InputFile';
import Textarea from '@src/Components/Atoms/Textarea/Textarea';
import { CloseIcon } from '@src/Components/Icons';

import { useCheckLocation } from '@src/Common/Hooks/useCheckLocation';
import { CustomStyleReturnType } from '@src/Components/Atoms/ButtonDefault/ButtonDefault.type';
import FolderIcon from '@src/Components/Icons/FolderIcon/FolderIcon';
import { ModalCloseIcon } from '@src/Components/Icons/ModalCloseIcon/ModalCloseIcon';
import { Form } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import DefaultModal from '../DefaultModal/DefaultModal';
import {
  BANNER_ACCEPT_ATTRIBUTE,
  BannerText,
  BannerUrlText,
  PlaceholderText,
  PlusIcon,
  tempDataDefault
} from './AddEventModal.constants';
import styles from './AddEventModal.module.scss';
import { IAddEventData, IAddEventModal } from './AddEventModal.type';
import useAddEventModal from './useAddEventModal';

const Loading = () => {
  return (
    <div className={styles['loading-wrapper']}>
      <div className={styles['loading']}>
        <CustomLoading />
      </div>
    </div>
  );
};

const ModalAlert: React.FC<{
  message: string;
  onClose?: () => void;
  handleCloseModal: (onClose?: (() => void) | undefined) => Promise<void>;
  handleViewMySubmission: () => void;
  isError?: boolean;
  isSuccess?: boolean;
  isLoading?: boolean;
}> = ({
  message,
  onClose,
  handleCloseModal,
  handleViewMySubmission,
  isError,
  isSuccess,
  isLoading
}) => {
  const textMessage = message ? `[${message}]` : '';

  return (
    <DefaultModal
      onClose={() => handleCloseModal(() => !!onClose && onClose())}
      show={isError || isSuccess}
      backgroundContent="transparent"
    >
      <section data-testid="modal-add-event" className={styles['Container']}>
        <header className={styles['Header']}>
          <h4>{(isError && 'Error') || (isSuccess && 'Success')}</h4>
          <button
            onClick={() => handleCloseModal(() => !!onClose && onClose())}
          >
            <ModalCloseIcon />
          </button>
        </header>
        <article
          className={styles['Content'] + ' ' + styles['ContentEventModal']}
        >
          <p>
            {isError ? (
              `Create event failed ${textMessage}`
            ) : (
              <div className={styles['SuccessCreatedEvent']}>
                <FolderIcon />
                <div className={styles['SuccessTextContent']}>
                  <div>The event has been reported.</div>
                  <div>Your report will undergo a verification process.</div>
                </div>
              </div>
            )}
          </p>
          <div className={styles['ButtonContainerModal']}>
            {isError ? (
              <ButtonDefault
                label="Close"
                disabled={isLoading}
                onClick={() => handleCloseModal(() => !!onClose && onClose())}
              />
            ) : (
              <ButtonDefault
                label="View My Submission"
                disabled={isLoading}
                onClick={handleViewMySubmission}
              />
            )}
          </div>
        </article>
      </section>
    </DefaultModal>
  );
};
const hasNoLatlong = (
  latitude: string,
  longitude: string
): {
  location: string;
  class: CustomStyleReturnType;
} => {
  if (latitude && latitude === '0' && longitude && longitude === '0') {
    return {
      location: 'Select Location',
      class: 'outline'
    };
  }

  return {
    location: 'Change Location',
    class: 'normal'
  };
};
const siteKey = process.env.REACT_APP_DUM_CAPTCHA_KEY ?? '';

const AddEventModal: React.FC<IAddEventModal> = observer(
  ({
    onClose,
    show = false,
    onChangeLocation,
    data = tempDataDefault as IAddEventData,
    locationName = '',
    isDetailVisible,
    existingId,
    closeDetailEvent
  }) => {
    const { DUMEventStore, FormStore } = useStores();
    const { isError, message, isLoading, isSuccess } =
      DUMEventStore.DUMEventDatas.statusCreate;

    const {
      isShow,
      handleClose,
      handleCloseModal,
      handleNumber,
      control,
      handleSubmit,
      getValues,
      errors,
      handleSubmitAdd,
      recaptchaRef,
      handleViewMySubmission
    } = useAddEventModal(data, show, existingId);

    const {
      setDataLat,
      setDataLong,
      isLoading: isLoadingFetchLocation,
      dataLocationName
    } = useCheckLocation(locationName, data.latitude, data.longitude);

    const handleDataLocationName = () => {
      return (
        <div className={styles['location-label']}>
          {isLoadingFetchLocation ? 'Loading ...' : dataLocationName}
        </div>
      );
    };

    return (
      <>
        <DefaultModal
          show={isShow}
          isBackgroundContainers={true}
          backgroundContent="transparent"
        >
          <section
            data-testid="modal-add-event"
            className={`${styles['Container']} ${styles['FormContainer']}`}
          >
            <header className={styles['Header']}>
              <h4>Add Event</h4>
              <button
                onClick={() => {
                  handleClose(() => onClose?.());
                  FormStore.selectedEvent = null;
                  if (isDetailVisible && closeDetailEvent) {
                    closeDetailEvent();
                  }
                }}
              >
                <CloseIcon />
              </button>
            </header>
            <article className={`have-scroll ${styles['Content']}`}>
              <div className={styles['FieldGrid']}>
                <div className={styles['FieldCell']}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Form.Item
                        help="Enter the event's name (50 chars or less)"
                        className={styles['EventNameContainer']}
                      >
                        <InputArea
                          label="Event Name"
                          placeholder={PlaceholderText.name}
                          maxLength={50}
                          value={value}
                          onChange={(e) => onChange(e.currentTarget.value)}
                          isError={!!errors.name}
                          messageErr={errors.name?.message}
                          onFocus={() => DUMEventStore.resetStatusCreate()}
                          disabled={isLoading}
                        />
                      </Form.Item>
                    )}
                  />
                </div>

                <div className={styles['FieldCell']}>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputDate
                        label="Event Date"
                        name="date"
                        value={value}
                        placeholder={PlaceholderText.date}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        error={errors.date?.message}
                        disabledMonth={isDetailVisible}
                        disabledYear={isDetailVisible}
                      />
                    )}
                  />
                </div>

                <div
                  className={`${styles['FieldCell']} ${styles['FieldCellFull']}`}
                >
                  <Controller
                    name="link"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputArea
                        label="Historical Event Link"
                        placeholder={PlaceholderText.link}
                        value={value}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        isError={!!errors.link}
                        messageErr={errors.link?.message}
                        onFocus={() => DUMEventStore.resetStatusCreate()}
                        disabled={isLoading}
                      />
                    )}
                  />
                </div>
              </div>
              <hr className={styles['Divider']} />
              <div className={styles['LatLongContainer']}>
                <div className={styles['LatLongHeadingTitle']}>
                  Where is the exact location?
                </div>
                <div className={styles['LatLongContent']}>
                  <Controller
                    name="latitude"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputArea
                        type="text"
                        label="Latitude"
                        placeholder="-00.000000"
                        value={value}
                        onChange={(e) => {
                          onChange(handleNumber(e.currentTarget.value));
                          setDataLat(e.currentTarget.value);
                        }}
                        isError={!!errors.latitude}
                        messageErr={errors.latitude?.message}
                        onFocus={() => DUMEventStore.resetStatusCreate()}
                        disabled={isLoading || isDetailVisible}
                      />
                    )}
                  />
                  <Controller
                    name="longitude"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputArea
                        type="text"
                        label="Longitude"
                        placeholder="-00.000000"
                        value={value}
                        onChange={(e) => {
                          onChange(handleNumber(e.currentTarget.value));
                          setDataLong(e.currentTarget.value);
                        }}
                        isError={!!errors.longitude}
                        messageErr={errors.longitude?.message}
                        onFocus={() => DUMEventStore.resetStatusCreate()}
                        disabled={isLoading || isDetailVisible}
                      />
                    )}
                  />
                </div>
              </div>
              {!isDetailVisible && (
                <ButtonDefault
                  onClick={() => {
                    if (onChangeLocation) onChangeLocation();
                    FormStore.temporaryData = {
                      name: getValues('name'),
                      date: getValues('date'),
                      link: getValues('link'),
                      longitude: getValues('longitude'),
                      latitude: getValues('latitude'),
                      description: getValues('description'),
                      banner: getValues('banner'),
                      bannerUrl: getValues('bannerUrl') ?? ''
                    };
                  }}
                  label={
                    <div className={styles['ChangeLocationButton']}>
                      <PlusIcon />
                      <span>
                        {
                          hasNoLatlong(FormStore.latitude, FormStore.longitude)
                            .location
                        }
                      </span>
                    </div>
                  }
                  customStyle={
                    hasNoLatlong(FormStore.latitude, FormStore.longitude).class
                  }
                />
              )}

              {isDetailVisible ? (
                <div className={styles['location-label']}>
                  {FormStore.selectedEvent?.country}
                </div>
              ) : (
                handleDataLocationName()
              )}

              <hr className={styles['Divider']} />
              <div className={`${styles['FieldGrid']} ${styles['MediaGrid']}`}>
                <div
                  className={`${styles['FieldCell']} ${styles['DescriptionCell']}`}
                >
                  <Controller
                    name="description"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles['text-area']}>
                        <Textarea
                          label="Description (Optional)"
                          placeholder={PlaceholderText.description}
                          value={value}
                          rows={6}
                          onChange={(e) => onChange(e.currentTarget.value)}
                          isError={!!errors.description}
                          messageErr={errors.description?.message}
                          onFocus={() => DUMEventStore.resetStatusCreate()}
                          disabled={isLoading}
                          maxLength={500}
                        />
                        <div className={styles['description-limit-text']}>
                          {500 - (value?.length ?? 0)}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className={styles['FieldCell']}>
                  <Controller
                    name="banner"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputFile
                        id="add-event-banner"
                        label={BannerText.label}
                        accept={BANNER_ACCEPT_ATTRIBUTE}
                        actionLabel={BannerText.action}
                        placeholder={BannerText.placeholder}
                        helpText={BannerText.help}
                        value={value ?? null}
                        onChange={(file) => onChange(file)}
                        isError={!!errors.banner}
                        messageErr={errors.banner?.message}
                        onFocus={() => DUMEventStore.resetStatusCreate()}
                        disabled={isLoading}
                      />
                    )}
                  />
                </div>

                <div className={styles['FieldCell']}>
                  <Controller
                    name="bannerUrl"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputArea
                        type="url"
                        label={BannerUrlText.label}
                        aria-label={BannerUrlText.label}
                        placeholder={BannerUrlText.placeholder}
                        value={value ?? ''}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        isError={!!errors.bannerUrl}
                        messageErr={errors.bannerUrl?.message}
                        onFocus={() => DUMEventStore.resetStatusCreate()}
                        disabled={isLoading}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={styles['ButtonContainer']}>
                <ButtonDefault
                  label="Cancel"
                  customStyle="outline"
                  background="transparent"
                  disabled={isLoading}
                  onClick={() =>
                    handleClose(() => {
                      if (onClose) {
                        onClose();
                      }
                    })
                  }
                />
                <ButtonDefault
                  label="Submit"
                  disabled={isLoading}
                  onClick={handleSubmit(handleSubmitAdd)}
                />
              </div>
            </article>
          </section>
          <ReCAPTCHA
            className={styles['recaptcha']}
            sitekey={siteKey}
            ref={recaptchaRef}
            size="invisible"
          />
        </DefaultModal>
        <ModalAlert
          message={message}
          onClose={onClose}
          handleCloseModal={handleCloseModal}
          handleViewMySubmission={handleViewMySubmission}
          isError={isError}
          isSuccess={isSuccess}
          isLoading={isLoading}
        />
        {isLoading ? <Loading /> : null}
      </>
    );
  }
);

export default AddEventModal;
